import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { getRentals, getRentalStats } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';
import { billingSummary, cycleTitle } from '../../utils/rentalBilling';
import {
  money, RentalPageHeader, RentalSourceChip, RentalStatCard, RentalStatusChip, tableSx
} from './rentalUi';

const Rentals = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [listRes, statsRes] = await Promise.all([
        getRentals({ search, status, source }),
        getRentalStats().catch(() => null)
      ]);
      setRows(listRes.data || []);
      setStats(statsRes?.data || null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load rentals'));
    } finally {
      setLoading(false);
    }
  }, [search, status, source]);

  useEffect(() => {
    load();
    const onBranch = () => load();
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  return (
    <Box>
      <RentalPageHeader
        title="Rentals"
        subtitle="Daily, weekly, and monthly contracts with invoices, returns, and collections in one place."
        actions={(
          <>
            <ExportButtons
              title="Rentals"
              filename="rentals"
              columns={[
                { label: 'Rental', value: (r) => dash(r.rental_number) },
                { label: 'Customer', value: (r) => dash(r.customer?.name) },
                { label: 'Type', value: (r) => dash(r.source_label || r.rental_source) },
                { label: 'Cycle', value: (r) => dash(cycleTitle(r.billing_cycle)) },
                { label: 'Start', value: (r) => fmtExportDate(r.start_date || r.rental_date) },
                { label: 'End', value: (r) => fmtExportDate(r.end_date || r.return_date) },
                { label: 'Status', value: (r) => fmtExportStatus(r.status) },
                { label: 'Balance', value: (r) => fmtExportMoney(r.balance) }
              ]}
              rows={rows}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/rentals/new')}>
              Create rental
            </Button>
          </>
        )}
      />

      {stats && (
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6} md={3}>
            <RentalStatCard label="Active" value={stats.activeRentals || 0} accent="success" />
          </Grid>
          <Grid item xs={6} md={3}>
            <RentalStatCard label="Overdue" value={stats.overdueRentals || 0} accent="error" />
          </Grid>
          <Grid item xs={6} md={3}>
            <RentalStatCard label="Pending invoices" value={stats.pendingInvoices || 0} accent="warning" />
          </Grid>
          <Grid item xs={6} md={3}>
            <RentalStatCard label="Overdue amount" value={money(stats.overdueAmount)} accent="error" />
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', p: 1.5, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search rental, customer, phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
        />
        <TextField
          select
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
          label="Status"
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="overdue">Overdue</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          sx={{ minWidth: 180 }}
          label="Type"
        >
          <MenuItem value="all">All types</MenuItem>
          <MenuItem value="sun_systems">Sun Systems</MenuItem>
          <MenuItem value="outside">Rent from Outside</MenuItem>
        </TextField>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>Rental</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">No rentals yet. Create one to get started.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/${r.id}`)}>
                  <TableCell>
                    <Typography fontWeight={700}>{r.rental_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <RentalSourceChip source={r.rental_source} label={r.source_label} />
                  </TableCell>
                  <TableCell>
                    {r.customer?.name || '—'}
                    {r.customer?.phone ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {r.customer.phone}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {(r.items || []).map((it) => it.product_name).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    {billingSummary(r.billing_cycle, r.duration_count)}
                  </TableCell>
                  <TableCell>
                    {r.start_date || r.rental_date} → {r.end_date || r.return_date}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {r.latest_invoice ? (
                      <>
                        <Button
                          size="small"
                          onClick={() => navigate(`/rentals/invoices/${r.latest_invoice.id}`)}
                        >
                          {r.latest_invoice.invoice_number}
                        </Button>
                        {r.invoice_count > 1 ? (
                          <Button
                            size="small"
                            onClick={() => navigate(`/rentals/invoices?rental_id=${r.id}`)}
                          >
                            All ({r.invoice_count})
                          </Button>
                        ) : null}
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No invoice</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={700} color={Number(r.balance) > 0 ? 'error.main' : 'success.main'}>
                      {money(r.balance)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <RentalStatusChip status={r.status} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button size="small" variant="outlined" onClick={() => navigate(`/rentals/${r.id}`)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Rentals;
